<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Icon from '@js/Components/Icon/Icon.vue'
import { getLocalizedUrl } from '@js/App/Plugins/LocalizedUrls'
import { getQueryParameter } from '@js/App/Utils/ReadQueryParams'
import { trans } from '@js/App/Plugins/Translations'

const CRITERION_KEYS = {
  location: 'CheckoutZeroMatch.CriterionLocation',
  max_rent: 'CheckoutZeroMatch.CriterionMaxRent',
  min_surface: 'CheckoutZeroMatch.CriterionMinSurface',
  min_bedrooms: 'CheckoutZeroMatch.CriterionMinBedrooms',
  combination: 'CheckoutZeroMatch.CriterionCombination'
}

// Absent prop = no warning. The server decides; this component never derives the
// verdict itself, so a stale or missing estimate can only ever show nothing.
const warning = computed(() => usePage().props.zeroMatchWarning ?? null)

// An unrecognised criterion token must not render half a sentence, so it falls
// back to the combination wording rather than to the raw token.
const criterionLine = computed(() => trans('CheckoutZeroMatch.Criterion', {
  criterion: trans(CRITERION_KEYS[warning.value?.criterion] ?? CRITERION_KEYS.combination)
}))

// plan travels so the widen event can be read per plan. uid deliberately does
// NOT: the widen route is session-only, because resolving a search profile from
// a renewal link would leak an id that reads the whole profile unauthenticated.
const widenUrl = computed(() => {
  const plan = getQueryParameter('plan')

  return getLocalizedUrl('checkout.widen-search', {}, plan ? { plan } : {})
})
</script>
<template>
  <!--
    Calm on purpose. This is a service to the buyer — we are telling them
    something we could have kept quiet about, right before they pay us — so the
    treatment is informative, never alarm: no red, no warning triangle, and the
    fix sits inside the notice instead of being left as an exercise. Changing the
    palette here changes the message (docs/paid-platform-barrier.md §3.0).
  -->
  <div
    v-if="warning"
    dusk="checkout-zero-match-notice"
    class="bg-blue-base-super-light border border-blue-base-light rounded-lg p-4 mb-5"
  >
    <div class="flex">
      <Icon name="light" class="text-purple-700 shrink-0 relative top-[2px]" />
      <div class="pl-3">
        <div class="text-purple-900 font-medium text-base mb-1">
          {{ $trans('CheckoutZeroMatch.Title') }}
        </div>
        <p class="text-purple-700 text-sm mb-1">
          {{ $trans('CheckoutZeroMatch.Body') }}
        </p>
        <p class="text-purple-700 text-sm mb-3">
          {{ criterionLine }}
        </p>
        <a
          :href="widenUrl"
          dusk="checkout-zero-match-widen"
          class="inline-block text-pink-500 text-sm font-medium underline"
        >
          {{ $trans('CheckoutZeroMatch.WidenCta') }}
        </a>
        <p class="text-gray-600 text-sm mt-2">
          {{ $trans('CheckoutZeroMatch.ContinueHint') }}
        </p>
      </div>
    </div>
  </div>
</template>
